import ReactGA from 'react-ga4';

export interface InitTrackingParameters {
  level: TrackingLevel;
  trackingIds: TrackingIdDetails;
}

interface TrackAddToCartParameters {
  addToCartResponses: Array<ICartAddItemResponse | undefined>;
  event: IEvent;
  currency: string;
}

class Tracking {
  private static instance: Tracking;
  private debug = false;
  private isMetaLoaded = false;
  private eventQueues: TrackingEventQueues = {
    ga: {
      platform: [],
      organisation: [],
      broker: [],
    },
    meta: {
      platform: [],
      organisation: [],
      broker: [],
    },
  };
  private trackers: Trackers = {
    ga: {
      platform: { isInitialized: false, trackingId: undefined },
      organisation: { isInitialized: false, trackingId: undefined },
      broker: { isInitialized: false, trackingId: undefined },
    },
    meta: {
      platform: { isInitialized: false, trackingId: undefined },
      organisation: { isInitialized: false, trackingId: undefined },
      broker: { isInitialized: false, trackingId: undefined },
    },
  };

  private constructor(debug?: boolean) {
    if (debug && !!window) {
      this.debug = debug;
      setInterval(() => {
        console.log(
          'Tracking debug',
          JSON.stringify({
            eventQueues: this.eventQueues,
            trackers: this.trackers,
          })
        );
      }, 10000);
    }
  }

  public static getInstance(options?: { debug: boolean }): Tracking {
    if (!Tracking.instance) {
      Tracking.instance = new Tracking(options?.debug);
    }
    return Tracking.instance;
  }

  private shouldAllowTracking() {
    if (!window) {
      console.warn('Tracking can be loaded only in the browser');
      return false;
    }
    if (window.location.hostname === 'localhost') {
      console.log('Tracking is disabled in localhost');
      return false;
    }
    return true;
  }

  /**
   * Initializes tracking for a certain level (on all required platforms)
   *
   * @param configs - An array of tracking configuration parameters.
   */
  public init(configs: InitTrackingParameters[]) {
    if (!this.shouldAllowTracking()) {
      return;
    }
    if (this.debug) {
      for (const c of configs) {
        console.log('init tracking', c.level, c.trackingIds);
      }
    }

    // Init GA tracking
    const googleConfigs = configs.filter(
      c =>
        c.trackingIds.TrackingGoogleID &&
        !this.trackers.ga[c.level].isInitialized
    );
    if (googleConfigs.length > 0) {
      ReactGA.initialize(
        googleConfigs.map(c => ({
          trackingId: c.trackingIds.TrackingGoogleID as string,
        }))
      );
      for (const c of googleConfigs) {
        this.trackers.ga[c.level].isInitialized = true;
        this.trackers.ga[c.level].trackingId =
          c.trackingIds.TrackingGoogleID ?? undefined;
        // NOTE: queue processing isn't supported yet
        if (this.debug) {
          console.log('init GA tracking done', c.level, c.trackingIds);
        }
      }
    }

    // Init Meta tracking
    const metaConfigs = configs.filter(
      c =>
        c.trackingIds.TrackingMetaID &&
        !this.trackers.meta[c.level].isInitialized
    );
    for (const c of metaConfigs) {
      if (!c.trackingIds.TrackingMetaID) {
        continue;
      }
      if (!this.isMetaLoaded) {
        this.loadMetaPixel();
      }
      this.initializeMetaPixel(c.trackingIds.TrackingMetaID);
      this.trackers.meta[c.level].isInitialized = true;
      this.trackers.meta[c.level].trackingId = c.trackingIds.TrackingMetaID;
      this.sendQueuedTrackingEvents('meta', c.level);
      if (this.debug) {
        console.log('init Meta tracking done', c.level, c.trackingIds);
      }
    }
  }

  private loadMetaPixel() {
    /* eslint-disable */
    // original Meta tracking code
    // @ts-ignore
    !(function (f, b, e, v, n, t, s) {
      // @ts-ignore
      if (f.fbq) return;
      // @ts-ignore
      n = f.fbq = function () {
        // @ts-ignore
        n.callMethod
          ? // @ts-ignore
            n.callMethod.apply(n, arguments)
          : // @ts-ignore
            n.queue.push(arguments);
      };
      // @ts-ignore
      if (!f._fbq) f._fbq = n;
      // @ts-ignore
      n.push = n;
      // @ts-ignore
      n.loaded = !0;
      // @ts-ignore
      n.version = '2.0';
      // @ts-ignore
      n.queue = [];
      // @ts-ignore
      t = b.createElement(e);
      // @ts-ignore
      t.async = !0;
      // @ts-ignore
      t.src = v;
      // @ts-ignore
      s = b.getElementsByTagName(e)[0];
      // @ts-ignore
      s.parentNode.insertBefore(t, s);
    })(
      window,
      document,
      'script',
      'https://connect.facebook.net/en_US/fbevents.js'
    );
    /* eslint-enable */
  }

  private initializeMetaPixel(metaTrackingId: string) {
    // original Meta tracking code
    fbq('init', metaTrackingId);
    fbq('trackSingle', metaTrackingId, 'PageView');
  }

  private queueEvent({
    event,
    level,
    platform,
  }: {
    event: TrackingEvent;
    level: TrackingLevel;
    platform: TrackingPlatform;
  }) {
    if (this.debug) {
      console.log('queue', { event, level, platform });
    }
    this.eventQueues[platform][level].push(event);
  }

  private sendQueuedTrackingEvents(
    platform: TrackingPlatform,
    level: TrackingLevel
  ) {
    const queues = this.eventQueues[platform];
    if (platform !== 'meta') {
      console.warn('Queuing for GA is not yet supported');
      return;
    }
    for (const event of queues[level as TrackingLevel]) {
      this.sendEvent({ event, level, platform });
    }
  }

  private sendEvent({
    event,
    GAparams,
    level,
    platform,
  }: {
    event: TrackingEvent;
    GAparams?: any;
    level?: TrackingLevel;
    platform: TrackingPlatform;
  }) {
    if (platform === 'ga') {
      let parameters = GAparams ?? {};
      if (level) {
        parameters.send_to = this.trackers.ga[level]?.trackingId;
      }
      if (typeof event !== 'string') {
        parameters = { ...parameters, ...event };
      }
      ReactGA.gtag(
        'event',
        typeof event === 'string' ? event : event.event,
        parameters
      );
    } else if (platform === 'meta') {
      if (typeof fbq !== 'function') {
        console.warn('Meta tracker was not initialized');
        return;
      }
      if (!level) {
        console.warn('Level must be defined to send Meta event');
        return;
      }
      const trackingId = this.trackers.meta[level]?.trackingId;
      if (!trackingId) {
        console.warn('Meta trackingId was not found for level', level);
        return;
      }
      let eventName: string;
      let parameters: Record<string, any> | undefined;
      if (typeof event === 'string') {
        eventName = event;
      } else {
        eventName = event.event;
        parameters = { ...event };
        delete parameters.event;
        if (Object.getOwnPropertyNames(parameters).length === 0) {
          parameters = undefined;
        }
      }
      if (this.debug) {
        console.log('send event to Meta', trackingId, eventName, parameters);
      }
      const result = fbq('trackSingle', trackingId, eventName, parameters);
      if (this.debug) {
        console.log('Meta tracking result', {
          trackingId,
          eventName,
          parameters,
          result,
        });
      }
    } else {
      console.warn('Unkown platform at sendEvent', platform);
    }
  }

  /**
   * Tracks a single event.
   *
   * If levels are not provided, it sends the event to all initialized levels.
   * If platforms are not provided, it sends the event to all initialized platforms.
   */
  public track({
    event,
    GAparams,
    levels,
    platforms,
  }: {
    event: TrackingEvent;
    GAparams?: any;
    levels?: TrackingLevel[];
    platforms?: TrackingPlatform[];
  }) {
    platforms = platforms ?? ['ga', 'meta'];
    levels = levels ?? ['platform', 'organisation', 'broker'];
    if (this.debug) {
      console.log('track event', { event, levels, platforms });
    }
    for (const platform of platforms) {
      for (const level of levels) {
        if (this.trackers[platform][level].isInitialized) {
          this.sendEvent({ event, GAparams, level, platform });
        } else {
          this.queueEvent({ event, level, platform });
        }
      }
    }
  }

  /**
   * Tracks a purchase event to GA and Meta based on Cart data
   */
  public trackPurchase(
    cart: ICartResponse,
    tickets: ITicketSummary[],
    event: IEvent,
    currency: string
  ) {
    if (this.debug) {
      console.log('track purchase', cart, currency);
    }

    const purchaseEventItems = tickets.map(item => ({
      item_category: 'Ticket',
      item_category2: 'Online',
      item_name: item.Name,
      item_id: item.ID,
      affiliation: event.Name,
      item_brand: event.OrganisationName,
      currency,
      coupon: cart.PromoCode,
      discount: cart.DiscountAmount,
      quantity: tickets.filter(t => t.ID === item.ID).length,
    }));

    const numItems = purchaseEventItems.reduce(
      (sum: number, item: { quantity?: number }) => sum + (item.quantity ?? 0),
      0
    );
    const purchaseContents = this.aggregateContents(
      cart.Items.map(item => item.ItemId)
    );
    const purchaseContentIds = purchaseContents.map(c => c.id);
    if (cart.TotalAmount) {
      this.track({
        event: 'purchase',
        GAparams: {
          value: cart.TotalAmount / 100,
          currency,
          items: purchaseEventItems.map(item => ({
            ...item,
            value:
              (cart.Items.find(cartItem => cartItem.ItemId === item.item_id)
                ?.TotalAmount ?? 0) / 100,
          })),
        },
        levels: ['platform'],
        platforms: ['ga'],
      });
      this.track({
        event: {
          event: 'Purchase',
          value: cart.TotalAmount / 100,
          currency,
          content_ids: purchaseContentIds,
          content_type: 'product',
          contents: purchaseContents,
          num_items: numItems,
        },
        levels: ['platform'],
        platforms: ['meta'],
      });
    }

    if (cart.OrganisationAmount) {
      this.track({
        event: 'purchase',
        GAparams: {
          value: cart.OrganisationAmount / 100,
          currency,
          items: purchaseEventItems.map(item => ({
            ...item,
            value:
              (cart.Items.find(cartItem => cartItem.ItemId === item.item_id)
                ?.OrganisationAmount ?? 0) / 100,
          })),
        },
        levels: ['organisation'],
        platforms: ['ga'],
      });
      this.track({
        event: {
          event: 'Purchase',
          value: cart.OrganisationAmount / 100,
          currency,
          content_ids: purchaseContentIds,
          content_type: 'product',
          contents: purchaseContents,
          num_items: numItems,
        },
        levels: ['organisation'],
        platforms: ['meta'],
      });
    }

    if (cart.BrokerAmount) {
      this.track({
        event: 'purchase',
        GAparams: {
          value: cart.BrokerAmount / 100,
          currency,
          items: purchaseEventItems.map(item => ({
            ...item,
            value:
              (cart.Items.find(cartItem => cartItem.ItemId === item.item_id)
                ?.BrokerAmount ?? 0) / 100,
          })),
        },
        levels: ['broker'],
        platforms: ['ga'],
      });
      this.track({
        event: {
          event: 'Purchase',
          value: cart.BrokerAmount / 100,
          currency,
          content_ids: purchaseContentIds,
          content_type: 'product',
          contents: purchaseContents,
          num_items: numItems,
        },
        levels: ['broker'],
        platforms: ['meta'],
      });
    }
  }

  public trackAddToCart({
    addToCartResponses,
    event,
    currency,
  }: TrackAddToCartParameters) {
    if (this.debug) {
      console.log('track add to cart', addToCartResponses, event);
    }

    const validResponses = addToCartResponses.filter(
      (r): r is ICartAddItemResponse => !!r
    );
    const lastResponse = validResponses[validResponses.length - 1];

    const addToCartEventParameters = {
      currency,
      coupon: lastResponse?.Cart?.PromoCode,
      items: validResponses.map(response => ({
        item_id: event.ID,
        item_name: event.Name,
        item_brand: event.OrganisationName,
        item_category: 'Event',
        item_category2: 'Online',
        item_variant: response.Item?.ItemId,
        currency,
        coupon: lastResponse?.Cart?.PromoCode,
        quantity: 1,
      })),
    };

    const platformTotal = lastResponse?.Cart?.TotalAmount ?? 0;
    const organisationTotal = lastResponse?.Cart?.OrganisationAmount ?? 0;
    const brokerTotal = lastResponse?.Cart?.BrokerAmount ?? 0;

    const addToCartItemIds = validResponses
      .map(r => r.Item?.ItemId)
      .filter((id): id is number => id !== undefined);
    const contents = this.aggregateContents(addToCartItemIds);
    const contentIds = contents.map(c => c.id);
    this.track({
      event: 'add_to_cart',
      GAparams: {
        ...addToCartEventParameters,
        value: platformTotal,
        items: addToCartEventParameters.items.map(item => ({
          ...item,
          value: validResponses.find(
            response => response.Item?.ItemId === item.item_variant
          )?.Item?.TotalAmount,
        })),
      },
      levels: ['platform'],
      platforms: ['ga'],
    });
    this.track({
      event: {
        event: 'AddToCart',
        value: platformTotal / 100,
        currency,
        content_ids: contentIds,
        content_type: 'product',
        contents,
      },
      levels: ['platform'],
      platforms: ['meta'],
    });

    this.track({
      event: 'add_to_cart',
      GAparams: {
        ...addToCartEventParameters,
        value: organisationTotal,
        items: addToCartEventParameters.items.map(item => ({
          ...item,
          value: validResponses.find(
            response => response.Item?.ItemId === item.item_variant
          )?.Item?.OrganisationAmount,
        })),
      },
      levels: ['organisation'],
      platforms: ['ga'],
    });
    this.track({
      event: {
        event: 'AddToCart',
        value: organisationTotal / 100,
        currency,
        content_ids: contentIds,
        content_type: 'product',
        contents,
      },
      levels: ['organisation'],
      platforms: ['meta'],
    });

    this.track({
      event: 'add_to_cart',
      GAparams: {
        ...addToCartEventParameters,
        value: brokerTotal,
        items: addToCartEventParameters.items.map(item => ({
          ...item,
          value: validResponses.find(
            response => response.Item?.ItemId === item.item_variant
          )?.Item?.BrokerAmount,
        })),
      },
      levels: ['broker'],
      platforms: ['ga'],
    });
    this.track({
      event: {
        event: 'AddToCart',
        value: brokerTotal / 100,
        currency,
        content_ids: contentIds,
        content_type: 'product',
        contents,
      },
      levels: ['broker'],
      platforms: ['meta'],
    });
  }

  public trackBeginCheckout(
    cartData: ICartResponse,
    currency: string,
    event: IEvent,
    promoCode?: string
  ) {
    if (this.debug) {
      console.log('track begin checkout', cartData, event, promoCode);
    }

    const beginCheckoutEventParameters = {
      currency,
      coupon: promoCode,
      items: cartData.Items.map(item => ({
        item_id: event.ID,
        item_name: event.Name,
        item_brand: event.OrganisationName,
        item_category: 'Event',
        item_category2: 'Online',
        item_variant: item.ItemId,
        currency,
        coupon: promoCode,
      })),
    };

    this.track({
      event: 'begin_checkout',
      GAparams: {
        ...beginCheckoutEventParameters,
        value: cartData.TotalAmount,
        items: beginCheckoutEventParameters.items.map(item => ({
          ...item,
          value: cartData.Items.find(
            cartItem => cartItem.ItemId === item.item_variant
          )?.TotalAmount,
        })),
      },
      levels: ['platform'],
      platforms: ['ga'],
    });
    this.track({
      event: 'begin_checkout',
      GAparams: {
        ...beginCheckoutEventParameters,
        value: cartData.OrganisationAmount,
        items: beginCheckoutEventParameters.items.map(item => ({
          ...item,
          value: cartData.Items.find(
            cartItem => cartItem.ItemId === item.item_variant
          )?.OrganisationAmount,
        })),
      },
      levels: ['organisation'],
      platforms: ['ga'],
    });
    this.track({
      event: 'begin_checkout',
      GAparams: {
        ...beginCheckoutEventParameters,
        value: cartData.BrokerAmount,
        items: beginCheckoutEventParameters.items.map(item => ({
          ...item,
          value: cartData.Items.find(
            cartItem => cartItem.ItemId === item.item_variant
          )?.BrokerAmount,
        })),
      },
      levels: ['broker'],
      platforms: ['ga'],
    });
  }

  private aggregateContents(itemIds: (number | undefined)[]) {
    const counts = new Map<string, number>();
    for (const id of itemIds) {
      if (id !== undefined) {
        const key = String(id);
        counts.set(key, (counts.get(key) ?? 0) + 1);
      }
    }
    return Array.from(counts.entries()).map(([id, quantity]) => ({
      id,
      quantity,
    }));
  }
}

// Note: change debug to false to disable debug logs and to true to enable them
export default Tracking.getInstance({ debug: false });
